import React, { useCallback, useEffect, useState } from 'react';
import { TagIcon, PlusIcon, SearchIcon, TrendingUpIcon, TrashIcon, AlertCircleIcon, CheckCircleIcon, PackageIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { supabaseAdmin } from '../lib/supabase';
import { Product, ProductPromotion } from '../types';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { useApp } from '../context/AppContext';
export function OfertasPage() {
  const {
    products: contextProducts,
    loadProducts
  } = useApp();
  const [promotions, setPromotions] = useState<ProductPromotion[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'todos' | 'oferta' | 'impulso'>('todos');
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Form state
  const [formProduct, setFormProduct] = useState('');
  const [formType, setFormType] = useState<'oferta' | 'impulso'>('oferta');
  const [formDiscount, setFormDiscount] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formCantidad, setFormCantidad] = useState('');
  const [formRegalo, setFormRegalo] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  // Sync context products to local state
  useEffect(() => {
    if (contextProducts.length > 0) {
      setProducts(contextProducts);
    }
  }, [contextProducts]);
  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load promotions
      const {
        data: promoData
      } = await supabaseAdmin.from('product_promotions').select('*').order('created_at', {
        ascending: false
      });
      if (promoData) {
        setPromotions(promoData.map((row: any) => ({
          id: row.id,
          productId: row.product_id,
          productName: row.product_name,
          productSku: row.product_sku,
          tipo: row.tipo,
          descuentoPct: row.descuento_pct,
          precioOferta: row.precio_oferta,
          activo: row.activo,
          fechaInicio: new Date(row.fecha_inicio),
          fechaFin: row.fecha_fin ? new Date(row.fecha_fin) : null,
          notas: row.notas,
          cantidad: row.cantidad || 0,
          regalo: row.regalo || ''
        })));
      }
      // Load products from shared context instead of independent fetch
      await loadProducts();
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, []);
  const loadDataCallback = useCallback(() => {
    loadData();
  }, []);
  useRealtimeRefresh(['product_promotions', 'products'], loadDataCallback);
  const filteredPromotions = promotions.filter(p => filterType === 'todos' || p.tipo === filterType).filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return p.productName.toLowerCase().includes(query) || p.productSku.toLowerCase().includes(query);
  });
  const activePromotions = promotions.filter(p => p.activo);
  const stats = {
    total: activePromotions.length,
    ofertas: activePromotions.filter(p => p.tipo === 'oferta').length,
    impulsos: activePromotions.filter(p => p.tipo === 'impulso').length
  };
  const resetForm = () => {
    setFormProduct('');
    setProductSearch('');
    setFormType('oferta');
    setFormDiscount('');
    setFormPrice('');
    setFormEndDate('');
    setFormNotes('');
    setFormCantidad('');
    setFormRegalo('');
    setError('');
  };
  const handleSubmit = async () => {
    if (!formProduct) {
      setError('Debe seleccionar un producto');
      return;
    }
    const selectedProd = products.find(p => p.id === formProduct);
    if (!selectedProd) return;
    setIsSubmitting(true);
    setError('');
    try {
      const {
        data,
        error: insertError
      } = await supabaseAdmin.from('product_promotions').insert({
        product_id: selectedProd.id,
        product_name: selectedProd.nombre,
        product_sku: selectedProd.sku,
        tipo: formType,
        descuento_pct: parseFloat(formDiscount) || 0,
        precio_oferta: parseFloat(formPrice) || 0,
        cantidad: parseInt(formCantidad) || 0,
        regalo: formRegalo || '',
        fecha_fin: formEndDate || null,
        notas: formNotes
      }).select().single();
      if (insertError) throw insertError;
      if (data) {
        setPromotions(prev => [{
          id: data.id,
          productId: data.product_id,
          productName: data.product_name,
          productSku: data.product_sku,
          tipo: data.tipo,
          descuentoPct: data.descuento_pct,
          precioOferta: data.precio_oferta,
          activo: data.activo,
          fechaInicio: new Date(data.fecha_inicio),
          fechaFin: data.fecha_fin ? new Date(data.fecha_fin) : null,
          notas: data.notas,
          cantidad: data.cantidad || 0,
          regalo: data.regalo || ''
        }, ...prev]);
      }
      setIsModalOpen(false);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la promoción');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Está seguro de eliminar esta promoción?')) return;
    try {
      const {
        error
      } = await supabaseAdmin.from('product_promotions').delete().eq('id', id);
      if (!error) {
        setPromotions(prev => prev.filter(p => p.id !== id));
      }
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };
  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const {
        error
      } = await supabaseAdmin.from('product_promotions').update({
        activo: !currentStatus
      }).eq('id', id);
      if (!error) {
        setPromotions(prev => prev.map(p => p.id === id ? {
          ...p,
          activo: !currentStatus
        } : p));
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };
  return <div className="space-y-6" data-id="element-2635">
      <PageHeader title="Ofertas e Impulsos" subtitle="Gestión de productos destacados para vendedores" icon={<TagIcon className="w-6 h-6" data-id="element-2637" />} data-id="element-2636">
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto" data-id="element-2638">
          <div className="relative w-full sm:w-64" data-id="element-2639">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2640" />
            <input type="text" placeholder="Buscar producto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" data-id="element-2641" />
          </div>
          <Button onClick={() => {
          resetForm();
          setIsModalOpen(true);
        }} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2643" />} data-id="element-2642">
            Nueva Promoción
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-stagger-1" data-id="element-2644">
        <StatsCard title="Total Activas" value={stats.total} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-2646" />} variant="primary" data-id="element-2645" />
        <StatsCard title="En Oferta" value={stats.ofertas} icon={<TagIcon className="w-5 h-5" data-id="element-2648" />} variant="success" data-id="element-2647" />
        <StatsCard title="Impulsos" value={stats.impulsos} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-2650" />} variant="warning" data-id="element-2649" />
      </div>

      <Card padding="none" className="animate-stagger-2" data-id="element-2651">
        <div className="flex border-b border-slate-200" data-id="element-2652">
          <button onClick={() => setFilterType('todos')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${filterType === 'todos' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-2653">
            Todos
          </button>
          <button onClick={() => setFilterType('oferta')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${filterType === 'oferta' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-2654">
            Ofertas
          </button>
          <button onClick={() => setFilterType('impulso')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${filterType === 'impulso' ? 'border-amber-600 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-2655">
            Impulsos
          </button>
        </div>

        {isLoading ? <div className="p-12 text-center" data-id="element-2656">
            <div className="w-8 h-8 border-3 border-slate-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" data-id="element-2657" />
            <p className="text-slate-500" data-id="element-2658">Cargando promociones...</p>
          </div> : filteredPromotions.length === 0 ? <div className="p-16 text-center" data-id="element-2659">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-2660">
              <TagIcon className="w-8 h-8 text-slate-400" data-id="element-2661" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-2662">
              No hay promociones
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-2663">
              Agrega productos en oferta o impulso para que los vendedores los
              ofrezcan primero.
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-2664">
            <table className="w-full text-sm" data-id="element-2665">
              <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2666">
                <tr data-id="element-2667">
                  <th className="px-5 py-3 text-left font-semibold text-slate-600" data-id="element-2668">
                    Producto
                  </th>
                  <th className="px-5 py-3 text-center font-semibold text-slate-600" data-id="element-2669">
                    Tipo
                  </th>
                  <th className="px-5 py-3 text-right font-semibold text-slate-600" data-id="element-2670">
                    Beneficio
                  </th>
                  <th className="px-5 py-3 text-center font-semibold text-slate-600" data-id="element-2671">
                    Cantidad
                  </th>
                  <th className="px-5 py-3 text-left font-semibold text-slate-600" data-id="element-2672">
                    Regalo
                  </th>
                  <th className="px-5 py-3 text-center font-semibold text-slate-600" data-id="element-2673">
                    Vigencia
                  </th>
                  <th className="px-5 py-3 text-center font-semibold text-slate-600" data-id="element-2674">
                    Estado
                  </th>
                  <th className="px-5 py-3 w-16" data-id="element-2675"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2676">
                {filteredPromotions.map(promo => <tr key={promo.id} className={`hover:bg-slate-50 transition-colors ${!promo.activo ? 'opacity-60' : ''}`} data-id="element-2677">
                    <td className="px-5 py-4" data-id="element-2678">
                      <p className="font-semibold text-slate-900" data-id="element-2679">
                        {promo.productName}
                      </p>
                      <p className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-2680">
                        {promo.productSku}
                      </p>
                      {promo.notas && <p className="text-xs text-slate-400 mt-1 italic" data-id="element-2681">
                          "{promo.notas}"
                        </p>}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-2682">
                      {promo.tipo === 'oferta' ? <Badge variant="success" size="sm" data-id="element-2683">
                          Oferta
                        </Badge> : <Badge variant="warning" size="sm" data-id="element-2684">
                          Impulso
                        </Badge>}
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-2685">
                      {promo.descuentoPct > 0 && <span className="block font-bold text-emerald-600" data-id="element-2686">
                          {promo.descuentoPct}% desc.
                        </span>}
                      {promo.precioOferta > 0 && <span className="block font-bold text-blue-600" data-id="element-2687">
                          RD${promo.precioOferta.toLocaleString()}
                        </span>}
                      {promo.descuentoPct === 0 && promo.precioOferta === 0 && <span className="text-slate-400" data-id="element-2688">—</span>}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-2689">
                      {promo.cantidad > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-blue-50 text-blue-700" data-id="element-2690">
                          {promo.cantidad}
                        </span> : <span className="text-slate-400" data-id="element-2691">—</span>}
                    </td>
                    <td className="px-5 py-4 text-left text-sm text-slate-700" data-id="element-2692">
                      {promo.regalo || <span className="text-slate-400" data-id="element-2693">—</span>}
                    </td>
                    <td className="px-5 py-4 text-center text-slate-600" data-id="element-2694">
                      <div className="text-xs" data-id="element-2695">
                        Desde: {promo.fechaInicio.toLocaleDateString('es-DO')}
                      </div>
                      {promo.fechaFin && <div className="text-xs mt-0.5" data-id="element-2696">
                          Hasta: {promo.fechaFin.toLocaleDateString('es-DO')}
                        </div>}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-2697">
                      <button onClick={() => handleToggleActive(promo.id, promo.activo)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${promo.activo ? 'bg-indigo-600' : 'bg-slate-300'}`} data-id="element-2698">
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${promo.activo ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-2699" />
                      </button>
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-2700">
                      <button onClick={() => handleDelete(promo.id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Eliminar" data-id="element-2701">
                        <TrashIcon className="w-4 h-4" data-id="element-2702" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Nueva Promoción" size="md" data-id="element-2703">
        <ModalBody data-id="element-2704">
          <div className="space-y-5" data-id="element-2705">
            {error && <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700 flex items-center gap-2" data-id="element-2706">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0" data-id="element-2707" />
                {error}
              </div>}

            {/* Product Search */}
            <div className="relative" data-id="element-2708">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2709">
                Producto *
              </label>
              <div className="relative" data-id="element-2710">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2711" />
                <input type="text" value={formProduct ? products.find(p => p.id === formProduct)?.nombre : productSearch} onChange={e => {
                setProductSearch(e.target.value);
                setFormProduct('');
                setShowProductDropdown(true);
              }} onFocus={() => setShowProductDropdown(true)} placeholder="Buscar producto..." className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2712" />
                {formProduct && <button onClick={() => {
                setFormProduct('');
                setProductSearch('');
              }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-2713">
                    ×
                  </button>}
              </div>

              {showProductDropdown && !formProduct && <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto" data-id="element-2714">
                  {products.filter(p => !productSearch || p.nombre.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase())).slice(0, 20).map(p => <button key={p.id} onClick={() => {
                setFormProduct(p.id);
                setShowProductDropdown(false);
              }} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-3" data-id="element-2715">
                        <PackageIcon className="w-4 h-4 text-slate-400" data-id="element-2716" />
                        <div data-id="element-2717">
                          <p className="text-sm font-medium text-slate-900" data-id="element-2718">
                            {p.nombre}
                          </p>
                          <p className="text-xs text-slate-500 font-mono" data-id="element-2719">
                            {p.sku}
                          </p>
                        </div>
                      </button>)}
                </div>}
              {showProductDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowProductDropdown(false)} data-id="element-2720" />}
            </div>

            {/* Type Selection */}
            <div data-id="element-2721">
              <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-2722">
                Tipo de Promoción *
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-2723">
                <button type="button" onClick={() => setFormType('oferta')} className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${formType === 'oferta' ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'border-slate-200 text-slate-500 hover:border-emerald-200'}`} data-id="element-2724">
                  Oferta
                </button>
                <button type="button" onClick={() => setFormType('impulso')} className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${formType === 'impulso' ? 'bg-amber-50 border-amber-500 text-amber-700' : 'border-slate-200 text-slate-500 hover:border-amber-200'}`} data-id="element-2725">
                  Impulso
                </button>
              </div>
            </div>

            {/* Pricing/Discount */}
            <div className="grid grid-cols-2 gap-4" data-id="element-2726">
              <Input label="Descuento (%)" type="number" placeholder="Ej: 10" value={formDiscount} onChange={e => setFormDiscount(e.target.value)} data-id="element-2727" />
              <Input label="Precio Especial (RD$)" type="number" placeholder="Ej: 1500" value={formPrice} onChange={e => setFormPrice(e.target.value)} data-id="element-2728" />
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-2729">
              <Input label="Cantidad" type="number" placeholder="Ej: 12" value={formCantidad} onChange={e => setFormCantidad(e.target.value)} data-id="element-2730" />
              <Input label="Regalo" placeholder="Ej: 1 unidad gratis" value={formRegalo} onChange={e => setFormRegalo(e.target.value)} data-id="element-2731" />
            </div>

            <Input label="Fecha de Fin (Opcional)" type="date" value={formEndDate} min={new Date().toISOString().split('T')[0]} onChange={e => setFormEndDate(e.target.value)} data-id="element-2732" />

            <div data-id="element-2733">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2734">
                Notas para el vendedor
              </label>
              <textarea className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-20 resize-none" placeholder="Ej: Ofrecer por compra mayor a 5 unidades..." value={formNotes} onChange={e => setFormNotes(e.target.value)} data-id="element-2735" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2736">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-2737">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={!formProduct} data-id="element-2738">
            Guardar Promoción
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}